import React, { useEffect, useMemo, useRef, forwardRef } from 'react';
import clsx from 'clsx';
import { Element } from '../element';
import DropdownTrigger from './components/trigger';
import DropdownItem from './components/item';
import DropdownContext from './context';
import DropdownContent from './components/content';
import { setRef } from '../../../hooks';
import * as styles from './dropdown.styles';

/** @type {import(".").DropdownType} */
const Dropdown = forwardRef(
  (
    { className, children, open = false, onClose, gapless = false, onOpen, position, keyboardCloseKeys = ['Escape', 'Tab'], align, ...props },
    ref,
  ) => {
    const innerRef = useRef(null);
    const triggerRef = useRef(null);
    const contextValue = useMemo(() => {
      return {
        isGapless: gapless,
        isOpen: open,
        show: (evt) => {
          if (open) {
            onClose?.(evt);
            return;
          }
          onOpen?.(evt);
        },
        hide: (evt) => {
          onClose?.(evt);
        },
        triggerRef,
        position,
        align,
        dropdownRef: innerRef,
      };
    }, [onClose, onOpen, open, gapless, position, align, innerRef]);

    useEffect(() => {
      if (!open) {
        return () => {};
      }
      const onClick = (evt) => {
        if (open && !innerRef.current.contains(evt.target)) {
          contextValue.hide(evt);
        }
      };
      const onKeyboardClose = (evt) => {
        if (keyboardCloseKeys.includes(evt.code)) {
          contextValue.hide(evt);
        }
      };

      window.addEventListener('click', onClick);
      window.addEventListener('keydown', onKeyboardClose);
      window.addEventListener('scroll', contextValue.hide);
      return () => {
        window.removeEventListener('click', onClick);
        window.removeEventListener('keydown', onKeyboardClose);
        window.removeEventListener('scroll', contextValue.hide);
      };
    }, [contextValue, open, keyboardCloseKeys, ref]);

    return (
      <Element as="div" ref={setRef(innerRef, ref)} className={clsx(styles.dropdown, { gapless }, className)} {...props}>
        <DropdownContext.Provider value={contextValue}>{children}</DropdownContext.Provider>
      </Element>
    );
  },
);

Dropdown.Trigger = DropdownTrigger;
Dropdown.Content = DropdownContent;
Dropdown.Context = DropdownContext;
Dropdown.Item = DropdownItem;

if (process.env.NODE_ENV !== 'production') {
  Dropdown.displayName = 'Dropdown';
}

export default Dropdown;
